var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { getFractionDimensions } from './helper';
import { GRID_SIZE } from '../constants/gridConstants';
export var SNAP_THRESHOLD = 55;
export var CIRCLE_SNAP_THRESHOLD = 160;
export var BORDER_OVERLAP = 0; // 0 = tiles touch edge-to-edge; border-right:none CSS hides the double border
var EPSILON = 1;
export var getSnappedPosition = function (currentPos, currentDimensions, currentShapeType, manipulatives, ignoreId, draggedGroup, newTileData) {
    var _a, _b;
    var result = {
        x: currentPos.x,
        y: currentPos.y,
        isSnappedX: false,
        isSnappedY: false,
        rotation: undefined,
        modifications: [],
    };
    var ignoreIds = Array.isArray(ignoreId) ? ignoreId : [ignoreId];
    var otherTiles = manipulatives.filter(function (m) {
        return m.data.type === 'fractionTiles' &&
            !ignoreIds.includes(m.data.id) &&
            m.data.shapeType === currentShapeType;
    });
    var draggedTileData = newTileData ||
        ((_a = manipulatives.find(function (m) { return m.data.id === ignoreIds[0]; })) === null || _a === void 0 ? void 0 : _a.data) ||
        ((_b = otherTiles[0]) === null || _b === void 0 ? void 0 : _b.data);
    if (currentShapeType === 'circle') {
        var snap = solveCircleSnap(currentPos, currentDimensions, otherTiles, draggedTileData);
        if (snap)
            return snap;
    }
    else {
        var snap = solveRectangleSnap(currentPos, currentDimensions, otherTiles, draggedGroup);
        if (snap)
            return snap;
    }
    return result;
};
var solveCircleSnap = function (pos, dims, targets, draggedData) {
    var _a, _b, _c;
    var myCenterX = pos.x + dims.width / 2;
    var myCenterY = pos.y + dims.height / 2;
    // Proximity gate: CIRCLE_SNAP_THRESHOLD (center-to-center) handles
    // both adjacency and stacking. No bounding-box edge gate needed —
    // sectors snap by sharing a center, not by edge adjacency.
    // Find closest target within threshold
    var closestTarget = null;
    var minDistSq = Math.pow(CIRCLE_SNAP_THRESHOLD, 2);
    for (var _i = 0, targets_1 = targets; _i < targets_1.length; _i++) {
        var m = targets_1[_i];
        var t = m.data;
        var tCenterX = t.center.x + t.dimensions.width / 2;
        var tCenterY = t.center.y + t.dimensions.height / 2;
        var distSq = Math.pow(myCenterX - tCenterX, 2) + Math.pow(myCenterY - tCenterY, 2);
        if (distSq < minDistSq) {
            minDistSq = distSq;
            closestTarget = t;
        }
    }
    if (!closestTarget)
        return null;
    // Target found
    var targetCenterX = closestTarget.center.x + closestTarget.dimensions.width / 2;
    var targetCenterY = closestTarget.center.y + closestTarget.dimensions.height / 2;
    var snappedX = targetCenterX - dims.width / 2;
    var snappedY = targetCenterY - dims.height / 2;
    // --- GAP-BASED SNAPPING LOGIC ---
    var tilesAtCenter = targets.filter(function (m) {
        var d = m.data;
        var cx = d.center.x + d.dimensions.width / 2;
        var cy = d.center.y + d.dimensions.height / 2;
        return Math.abs(cx - targetCenterX) < 10 && Math.abs(cy - targetCenterY) < 10;
    });
    var fractionSize = (draggedData === null || draggedData === void 0 ? void 0 : draggedData.fractionSize) || 'fraction-1';
    var myConfig = ((_a = getFractionDimensions(fractionSize)) === null || _a === void 0 ? void 0 : _a.circle) || {
        start: 0,
        sweep: 360,
    };
    var myRotation = (_b = draggedData === null || draggedData === void 0 ? void 0 : draggedData.rotation) !== null && _b !== void 0 ? _b : 0;
    var mySweep = (_c = draggedData === null || draggedData === void 0 ? void 0 : draggedData.customSweep) !== null && _c !== void 0 ? _c : myConfig.sweep;
    if (mySweep >= 360) {
        return {
            x: snappedX,
            y: snappedY,
            isSnappedX: true,
            isSnappedY: true,
            rotation: 0,
            modifications: [],
        };
    }
    // 1. Get all occupied segments [start, end]
    var segments = tilesAtCenter.map(function (m) {
        var _a, _b, _c, _d;
        var d = m.data;
        var c = ((_a = getFractionDimensions(d.fractionSize)) === null || _a === void 0 ? void 0 : _a.circle) || {
            start: 0,
            sweep: 360,
        };
        var sw = (_b = d.customSweep) !== null && _b !== void 0 ? _b : c.sweep;
        // Glued tiles store rotation as the absolute start angle.
        // Normal tiles store rotation as an offset applied on top of c.start.
        var s = d.customSweep != null
            ? ((((_c = d.rotation) !== null && _c !== void 0 ? _c : 0) % 360) + 360) % 360
            : (((c.start + ((_d = d.rotation) !== null && _d !== void 0 ? _d : 0)) % 360) + 360) % 360;
        return { start: s, end: (s + sw) % 360, sweep: sw };
    });
    // 2. Identify Gaps
    // First, normalise segments to their angular union so that tiles stacked in
    // multiple layers (duplicate segments at the same angle) don't produce phantom
    // gaps. We use a 1°-resolution boolean array for simplicity; all standard
    // fraction sweeps (30°, 36°, 45°, 60°, 72°, 90°, 120°, 180°, 360°) are
    // integer degrees, so 1° resolution is exact.
    var covered = new Array(360).fill(0);
    for (var _d = 0, segments_1 = segments; _d < segments_1.length; _d++) {
        var seg = segments_1[_d];
        var sw = Math.min(Math.ceil(seg.sweep), 360);
        for (var i = 0; i < sw; i++) {
            covered[Math.round((seg.start + i) % 360)] = 1;
        }
    }
    var normalizedSegments = [];
    var inSeg = false;
    var normSegStart = 0;
    for (var i = 0; i < 360; i++) {
        if (covered[i] && !inSeg) {
            inSeg = true;
            normSegStart = i;
        }
        else if (!covered[i] && inSeg) {
            inSeg = false;
            normalizedSegments.push({ start: normSegStart, end: i, sweep: i - normSegStart });
        }
    }
    if (inSeg)
        normalizedSegments.push({ start: normSegStart, end: 0, sweep: 360 - normSegStart });
    var sorted = __spreadArray([], normalizedSegments, true).sort(function (a, b) { return a.start - b.start; });
    var gaps = [];
    if (sorted.length === 0) {
        gaps.push({ start: 0, end: 0, sweep: 360 });
    }
    else {
        for (var i = 0; i < sorted.length; i++) {
            var current = sorted[i];
            var next = sorted[(i + 1) % sorted.length];
            var gapStart = current.end;
            var gapEnd = next.start;
            var gapSweep = (gapEnd - gapStart + 360) % 360;
            if (gapSweep > EPSILON) {
                gaps.push({ start: gapStart, end: gapEnd, sweep: gapSweep });
            }
        }
    }
    // 3. Find Best Snap Point
    var dragAngle = (Math.atan2(myCenterY - targetCenterY, myCenterX - targetCenterX) * 180) / Math.PI;
    dragAngle = ((dragAngle % 360) + 360) % 360;
    var bestRotation = myRotation;
    var minAngularDiff = 360;
    var foundSnap = false;
    // Whether the dragged tile is a glued tile (rotation = absolute start, not offset)
    var draggedIsGlued = (draggedData === null || draggedData === void 0 ? void 0 : draggedData.customSweep) != null;
    // Option A / B: fill a gap (existing behaviour)
    for (var _e = 0, gaps_1 = gaps; _e < gaps_1.length; _e++) {
        var gap = gaps_1[_e];
        if (gap.sweep + EPSILON >= mySweep) {
            // Option A: Align my start to gap start
            var rotA = draggedIsGlued
                ? ((gap.start % 360) + 360) % 360
                : (((gap.start - myConfig.start) % 360) + 360) % 360;
            var midA = (gap.start + mySweep / 2) % 360;
            var diffA = Math.abs(((dragAngle - midA + 540) % 360) - 180);
            if (diffA < minAngularDiff) {
                minAngularDiff = diffA;
                bestRotation = rotA;
                foundSnap = true;
            }
            // Option B: Align my end to gap end
            var myStartForB = (gap.end - mySweep + 360) % 360;
            var rotB = draggedIsGlued
                ? ((myStartForB % 360) + 360) % 360
                : (((myStartForB - myConfig.start) % 360) + 360) % 360;
            var midB = (myStartForB + mySweep / 2) % 360;
            var diffB = Math.abs(((dragAngle - midB + 540) % 360) - 180);
            if (diffB < minAngularDiff) {
                minAngularDiff = diffB;
                bestRotation = rotB;
                foundSnap = true;
            }
        }
    }
    // Option C: stack on top of an existing sector.
    // This competes with gap-fill: the drag angle decides which wins.
    // If the user drags toward an occupied sector the tile stacks there;
    // if toward an empty gap it fills the gap.
    for (var _f = 0, normalizedSegments_1 = normalizedSegments; _f < normalizedSegments_1.length; _f++) {
        var seg = normalizedSegments_1[_f];
        var rotC = draggedIsGlued
            ? ((seg.start % 360) + 360) % 360
            : (((seg.start - myConfig.start) % 360) + 360) % 360;
        // Compare angular distance of drag angle to the midpoint of where
        // the dragged tile would sit when start-aligned with this segment.
        var midC = (seg.start + mySweep / 2) % 360;
        var diffC = Math.abs(((dragAngle - midC + 540) % 360) - 180);
        if (diffC < minAngularDiff) {
            minAngularDiff = diffC;
            bestRotation = rotC;
            foundSnap = true;
        }
    }
    if (!foundSnap) {
        // Fallback: no gap and no existing sector matched — shouldn't normally
        // reach here, but return null so the caller can handle it gracefully.
        return null;
    }
    return {
        x: snappedX,
        y: snappedY,
        isSnappedX: true,
        isSnappedY: true,
        rotation: bestRotation,
        modifications: [],
    };
};
var solveRectangleSnap = function (pos, dims, targets, draggedGroup) {
    var _a;
    var myCenterY = pos.y + dims.height / 2;
    // Edge-to-edge proximity gate: only enter snap logic when at least one
    // edge of the dragged tile (or group) is within EDGE_SNAP_THRESHOLD of
    // a target tile's edge. This allows stacking when no edges are close.
    // Match SNAP_THRESHOLD so the gate doesn't reject valid drops that are
    // within the actual snap distance (previously 10 caused misses at 11-54px).
    var EDGE_SNAP_THRESHOLD = SNAP_THRESHOLD;
    var checkLeft = pos.x;
    var checkRight = pos.x + dims.width;
    var checkTop = pos.y;
    var checkBottom = pos.y + dims.height;
    if (draggedGroup && draggedGroup.length > 0) {
        var minX = Math.min.apply(Math, draggedGroup.map(function (g) { return g.offset.x; }));
        var maxX = Math.max.apply(Math, draggedGroup.map(function (g) { return g.offset.x + g.dimensions.width; }));
        var minY = Math.min.apply(Math, draggedGroup.map(function (g) { return g.offset.y; }));
        var maxY = Math.max.apply(Math, draggedGroup.map(function (g) { return g.offset.y + g.dimensions.height; }));
        checkLeft = pos.x + minX;
        checkRight = pos.x + maxX;
        checkTop = pos.y + minY;
        checkBottom = pos.y + maxY;
    }
    var hasCloseEdge = false;
    for (var _i = 0, targets_2 = targets; _i < targets_2.length; _i++) {
        var m = targets_2[_i];
        var t = m.data;
        var tLeft = t.center.x;
        var tRight = t.center.x + t.dimensions.width;
        var tTop = t.center.y;
        var tBottom = t.center.y + t.dimensions.height;
        var vertOverlap = checkBottom > tTop - EDGE_SNAP_THRESHOLD && checkTop < tBottom + EDGE_SNAP_THRESHOLD;
        var horizOverlap = checkRight > tLeft - EDGE_SNAP_THRESHOLD && checkLeft < tRight + EDGE_SNAP_THRESHOLD;
        // Gap = actual space between opposite edges.
        // When separated, one gap is positive (real distance), the other is negative (clamped to 0).
        // Use Math.max to pick the real gap. When overlapping, both clamp to 0.
        var hDist = Math.max(0, tLeft - checkRight, // positive when dragged is left of target
        checkLeft - tRight);
        var vDist = Math.max(0, tTop - checkBottom, // positive when dragged is above target
        checkTop - tBottom);
        var hClose = vertOverlap && hDist <= EDGE_SNAP_THRESHOLD;
        var vClose = horizOverlap && vDist <= EDGE_SNAP_THRESHOLD;
        if (hClose || vClose) {
            hasCloseEdge = true;
            break;
        }
    }
    // Determine if the dragged tile is above or below all targets.
    // If so, prefer above/below anchors over same-row to prevent
    // horizontal snapping when approaching vertically.
    var isAboveOrBelow = false;
    if (hasCloseEdge) {
        var allTargetTop = Math.min.apply(Math, targets.map(function (m) { return m.data.center.y; }));
        var allTargetBottom = Math.max.apply(Math, targets.map(function (m) {
            var d = m.data;
            return d.center.y + d.dimensions.height;
        }));
        isAboveOrBelow = myCenterY < allTargetTop || myCenterY > allTargetBottom;
    }
    // Compute groupLeftOffset early — needed by both the stacking check and the
    // chain-insertion logic below.
    var groupLeftOffset = 0;
    if (draggedGroup && draggedGroup.length > 0) {
        var rowGroup = draggedGroup.filter(function (g) { return Math.abs(g.offset.y) < 10; });
        if (rowGroup.length > 0) {
            groupLeftOffset = Math.min.apply(Math, rowGroup.map(function (g) { return g.offset.x; }));
        }
    }
    var _loop_1 = function (m) {
        var t = m.data;
        if (pos.x >= t.center.x &&
            pos.x <= t.center.x + t.dimensions.width &&
            pos.y >= t.center.y &&
            pos.y <= t.center.y + t.dimensions.height) {
            var stackY_1 = t.center.y;
            // All tiles in this row (same Y), sorted left to right.
            var allRowTiles = targets
                .map(function (m2) { return m2.data; })
                .filter(function (d) { return Math.abs(d.center.y - stackY_1) < 5; })
                .sort(function (a, b) { return a.center.x - b.center.x; });
            var leftMostX = allRowTiles.length > 0 ? allRowTiles[0].center.x : t.center.x;
            // Snap candidates: left edge of leftmost + right edge of every tile in the row.
            var snapCandidates = [leftMostX - groupLeftOffset];
            for (var _l = 0, allRowTiles_1 = allRowTiles; _l < allRowTiles_1.length; _l++) {
                var st = allRowTiles_1[_l];
                snapCandidates.push(st.center.x + st.dimensions.width - groupLeftOffset);
            }
            // Pick the candidate whose snapped position is closest to where the tile currently is.
            var bestSnapX = snapCandidates[0];
            var minDist = Math.abs(pos.x - snapCandidates[0]);
            for (var _m = 0, snapCandidates_1 = snapCandidates; _m < snapCandidates_1.length; _m++) {
                var candidate = snapCandidates_1[_m];
                var dist = Math.abs(pos.x - candidate);
                if (dist < minDist) {
                    minDist = dist;
                    bestSnapX = candidate;
                }
            }
            return { value: {
                    x: bestSnapX,
                    y: stackY_1,
                    isSnappedX: true,
                    isSnappedY: true,
                    rotation: undefined,
                    modifications: [],
                } };
        }
    };
    // Stacking: if dragged tile's center is inside a target tile,
    // snap to the closest edge among all tiles already in that row
    // (left of leftmost OR right edge of any stacked tile).
    for (var _b = 0, targets_3 = targets; _b < targets_3.length; _b++) {
        var m = targets_3[_b];
        var state_1 = _loop_1(m);
        if (typeof state_1 === "object")
            return state_1.value;
    }
    if (!hasCloseEdge) {
        return null;
    }
    // Find closest target and best anchor (current row, top, or bottom)
    var bestTargetY = null;
    var minDy = SNAP_THRESHOLD;
    var minDx = Infinity;
    var closestTileData = null;
    for (var _c = 0, targets_4 = targets; _c < targets_4.length; _c++) {
        var m = targets_4[_c];
        var t = m.data;
        // Possible vertical alignment anchors based on actual tile heights
        // - t.center.y: Align on the same row (top edges aligned)
        // - t.center.y - dims.height: Place current tile exactly ABOVE target
        // - t.center.y + t.dimensions.height: Place current tile exactly BELOW target
        var aboveY = t.center.y - dims.height;
        var belowY = t.center.y + t.dimensions.height;
        var sameRowY = t.center.y;
        // When the edge gate detected vertical adjacency (above/below),
        // only consider above/below anchors — skip same-row to prevent
        // square tiles from always snapping sideways.
        var possibleYAnchors = isAboveOrBelow ? [aboveY, belowY] : [sameRowY, aboveY, belowY];
        // Left-edge based horizontal distance — using tile centers here would
        // bias toward the middle of the target when the dragged tile's width
        // differs a lot from the target's (e.g. a wide "1" tile dropped above
        // a row of narrow "1/12" tiles lands its center several tiles in).
        var dx = Math.abs(pos.x - t.center.x);
        for (var _d = 0, possibleYAnchors_1 = possibleYAnchors; _d < possibleYAnchors_1.length; _d++) {
            var anchorY = possibleYAnchors_1[_d];
            var dy = Math.abs(pos.y - anchorY);
            if (dy < minDy - EPSILON) {
                // Strictly closer vertically — always pick
                minDy = dy;
                minDx = dx;
                bestTargetY = anchorY;
                closestTileData = t;
            }
            else if (dy < minDy + EPSILON && dx < minDx) {
                // Same vertical distance — pick the one closer horizontally
                minDy = dy;
                minDx = dx;
                bestTargetY = anchorY;
                closestTileData = t;
            }
        }
    }
    if (!closestTileData || bestTargetY === null) {
        return null;
    }
    var targetY = bestTargetY;
    var ROW_Y_TOL = 5; // strict y-alignment tolerance
    var CHAIN_TOL = 5; // adjacency tolerance for connected chains
    // Strict row detection: only include tiles whose top edge is closely aligned
    // with targetY (prevents pulling in tiles from adjacent rows).
    // Deduplicate stacked tiles: if a tile's X range overlaps an already-kept
    // tile's X range, it's stacked on top — skip it so the insertion logic
    // doesn't spread stacked tiles out horizontally.
    var rowTilesRaw = targets
        .map(function (t) { return t.data; })
        .filter(function (d) { return Math.abs(d.center.y - targetY) < ROW_Y_TOL; })
        .sort(function (a, b) { return a.center.x - b.center.x; });
    var rowTiles = [];
    var _loop_2 = function (tile) {
        var tileLeft = tile.center.x;
        var tileRight = tile.center.x + tile.dimensions.width;
        var isStacked = rowTiles.some(function (existing) {
            var exLeft = existing.center.x;
            var exRight = existing.center.x + existing.dimensions.width;
            // Overlapping X ranges = stacked (ignore BORDER_OVERLAP between adjacent tiles)
            var overlap = Math.min(tileRight, exRight) - Math.max(tileLeft, exLeft);
            return overlap > BORDER_OVERLAP + EPSILON;
        });
        if (!isStacked)
            rowTiles.push(tile);
    };
    for (var _e = 0, rowTilesRaw_1 = rowTilesRaw; _e < rowTilesRaw_1.length; _e++) {
        var tile = rowTilesRaw_1[_e];
        _loop_2(tile);
    }
    var virtualItem = { id: 'NEW', center: { x: 0, y: targetY }, dimensions: dims };
    if (draggedGroup && draggedGroup.length > 0) {
        var rowGroup = draggedGroup.filter(function (g) { return Math.abs(g.offset.y) < 10; });
        if (rowGroup.length > 0) {
            // Find leftmost and rightmost relative to leader
            var minX = Math.min.apply(Math, rowGroup.map(function (g) { return g.offset.x; }));
            var maxX = Math.max.apply(Math, rowGroup.map(function (g) { return g.offset.x + g.dimensions.width; }));
            var totalWidth = maxX - minX;
            virtualItem = {
                id: 'NEW_GROUP',
                center: { x: 0, y: targetY },
                dimensions: { width: totalWidth, height: dims.height },
            };
        }
    }
    // Horizontal snapping logic
    // If there ARE tiles in this target row, we use the insertion logic
    if (rowTiles.length > 0) {
        // --- CONNECTED CHAIN DETECTION ---
        // Build chains of adjacent tiles so we only snap into the nearest chain,
        // not ALL tiles in the row (prevents pulling in distant tiles).
        var chains = [];
        var currentChain = [rowTiles[0]];
        for (var i = 1; i < rowTiles.length; i++) {
            var prevRight = rowTiles[i - 1].center.x + rowTiles[i - 1].dimensions.width;
            if (Math.abs(prevRight - rowTiles[i].center.x) < CHAIN_TOL) {
                currentChain.push(rowTiles[i]);
            }
            else {
                chains.push(currentChain);
                currentChain = [rowTiles[i]];
            }
        }
        chains.push(currentChain);
        // Find the chain closest to the dragged tile
        var bestChain = chains[0];
        var bestDist = Infinity;
        for (var _f = 0, chains_1 = chains; _f < chains_1.length; _f++) {
            var chain = chains_1[_f];
            var chainLeft_1 = chain[0].center.x;
            var chainRight_1 = chain[chain.length - 1].center.x + chain[chain.length - 1].dimensions.width;
            // Bounding-box overlap distance (using the dragged tile's actual left/
            // right edges), not center distance — a wide dragged tile overlapping
            // a chain by its edges should count as "at" that chain even if its
            // center falls outside it.
            var dist = void 0;
            if (checkRight < chainLeft_1) {
                dist = chainLeft_1 - checkRight;
            }
            else if (checkLeft > chainRight_1) {
                dist = checkLeft - chainRight_1;
            }
            else {
                dist = 0;
            }
            if (dist < bestDist) {
                bestDist = dist;
                bestChain = chain;
            }
        }
        // Use the closest chain for insertion (not all rowTiles)
        var snapTiles = bestChain;
        // Left-edge based: insert before the first tile whose left edge is to
        // the right of the dragged tile's left edge. For equal-width tiles this
        // matches the old center-based midpoint comparison exactly; for tiles of
        // very different widths (e.g. a "1" tile over a row of "1/12" tiles) it
        // correctly inserts at the position implied by the dragged tile's own
        // left edge instead of drifting toward wherever its center lands.
        var insertIndex = 0;
        for (var i = 0; i < snapTiles.length; i++) {
            var tile = snapTiles[i];
            if (pos.x < tile.center.x) {
                insertIndex = i;
                break;
            }
            insertIndex = i + 1;
        }
        // Bounds of the snap chain — used as the adjacency snap position.
        var chainLeft = snapTiles[0].center.x;
        var chainRight = snapTiles[snapTiles.length - 1].center.x +
            snapTiles[snapTiles.length - 1].dimensions.width;
        // Corner-align reference: when placing above/below the target row, use
        // closestTileData's own bounds (the specific tile being bordered), not the
        // full row. This keeps the ghost aligned with the tile directly above/below
        // rather than spanning to unrelated tiles in that row (e.g. a separate 1/10
        // at the far right of the same row as 1/2). When inserting into the SAME
        // row, use chain bounds.
        var isAboveBelow = Math.abs(targetY - closestTileData.center.y) > ROW_Y_TOL;
        var govLeft = isAboveBelow ? closestTileData.center.x : chainLeft;
        var govRight = isAboveBelow
            ? closestTileData.center.x + closestTileData.dimensions.width
            : chainRight;
        // Bounding-box overlap (dragged tile's actual left/right edges vs. the
        // governing range) rather than center-in-range — a wide dragged tile can
        // have its center fall outside govLeft/govRight while its edges are
        // still legitimately within snapping distance.
        var withinChainBounds = checkRight >= govLeft - SNAP_THRESHOLD && checkLeft <= govRight + SNAP_THRESHOLD;
        // When inserting at the edges of a chain (before first or after last),
        // place the dragged tile next to the chain without recalculating existing
        // tile positions. This prevents floating-point drift from pushing tiles.
        // Also offer a corner-aligned alternative (left-align or right-align with
        // the chain bounds) so the tile can snap to any of the four corners
        // (top-left, top-right, bottom-left, bottom-right), whichever is nearest.
        if (insertIndex === 0) {
            var chainStartX = snapTiles[0].center.x - virtualItem.dimensions.width + BORDER_OVERLAP - groupLeftOffset;
            // Left-align with the governing row's left edge as an additional
            // candidate — but ONLY meaningful when bordering a genuinely separate
            // row above/below (isAboveBelow), where govLeft is that row's own
            // single-tile reference. For same-row insertion, govLeft === chainLeft,
            // so this candidate would mean "overlap the chain's own first tile" —
            // not a real position — and could incorrectly win over the correct
            // flush-attach position (chainStartX), producing an unwanted gap.
            var parentLeftX = govLeft - groupLeftOffset;
            var leaderSnappedX_1 = isAboveBelow && Math.abs(pos.x - parentLeftX) <= Math.abs(pos.x - chainStartX)
                ? parentLeftX
                : chainStartX;
            if (!withinChainBounds && Math.abs(pos.x - leaderSnappedX_1) > SNAP_THRESHOLD)
                return null;
            return {
                x: leaderSnappedX_1,
                y: targetY,
                isSnappedX: true,
                isSnappedY: true,
                rotation: undefined,
                modifications: [],
            };
        }
        if (insertIndex === snapTiles.length) {
            var lastTile = snapTiles[snapTiles.length - 1];
            var chainEndX = lastTile.center.x + lastTile.dimensions.width - BORDER_OVERLAP - groupLeftOffset;
            // Right-align with the governing row's right edge as an additional
            // candidate — same caveat as parentLeftX above: only meaningful when
            // isAboveBelow (bordering a separate row), not for same-row append.
            var parentRightX = govRight - virtualItem.dimensions.width - groupLeftOffset;
            var leaderSnappedX_2 = isAboveBelow && Math.abs(pos.x - parentRightX) <= Math.abs(pos.x - chainEndX)
                ? parentRightX
                : chainEndX;
            if (!withinChainBounds && Math.abs(pos.x - leaderSnappedX_2) > SNAP_THRESHOLD)
                return null;
            return {
                x: leaderSnappedX_2,
                y: targetY,
                isSnappedX: true,
                isSnappedY: true,
                rotation: undefined,
                modifications: [],
            };
        }
        // Mid-chain insertion: recalculate positions to make room
        var ordered = __spreadArray(__spreadArray(__spreadArray([], snapTiles.slice(0, insertIndex), true), [
            virtualItem
        ], false), snapTiles.slice(insertIndex), true);
        var refX = ordered[0].center.x;
        var modifications = [];
        var leaderSnappedX = pos.x;
        var currentX = refX;
        var _loop_3 = function (item) {
            if (item.id === 'NEW' || item.id === 'NEW_GROUP') {
                leaderSnappedX = currentX - groupLeftOffset;
            }
            else {
                // Check if this item is locked. If it's locked and needs to move, the snap is invalid.
                var originalItem = snapTiles.find(function (rt) { return rt.id === item.id; });
                if (originalItem && ((_a = originalItem.lock) === null || _a === void 0 ? void 0 : _a.positioning)) {
                    if (Math.abs(originalItem.center.x - currentX) > EPSILON) {
                        return { value: null };
                    }
                }
                if (originalItem && Math.abs(originalItem.center.x - currentX) > EPSILON) {
                    modifications.push({
                        id: item.id,
                        center: { x: currentX, y: targetY },
                    });
                }
            }
            currentX += item.dimensions.width - BORDER_OVERLAP;
        };
        for (var _g = 0, ordered_1 = ordered; _g < ordered_1.length; _g++) {
            var item = ordered_1[_g];
            var state_2 = _loop_3(item);
            if (typeof state_2 === "object")
                return state_2.value;
        }
        // --- STRICT HORIZONTAL CHECK ---
        if (Math.abs(pos.x - leaderSnappedX) > SNAP_THRESHOLD) {
            return null;
        }
        return {
            x: leaderSnappedX,
            y: targetY,
            isSnappedX: true,
            isSnappedY: true,
            rotation: undefined,
            modifications: modifications,
        };
    }
    else {
        // No tiles in the target row. Offer four corner snap positions relative to the
        // overall bounds of all target tiles (the row above or below):
        //   left-align  → dragged tile's left edge = leftmost target's left edge
        //   right-align → dragged tile's right edge = rightmost target's right edge
        // Pick whichever is nearest to the drop position.
        // Use the bounds of the CONNECTED CHAIN closestTileData belongs to (not
        // just that one tile) as the corner reference. A single tile's bounds
        // works fine when the dragged tile is a similar size, but collapses when
        // the dragged tile spans an entire row of much smaller tiles (e.g. a "1"
        // tile above a row of twelve "1/12" tiles): closestTileData ends up
        // being whichever small tile is nearest, and right-aligning the wide
        // dragged tile to that one tile's right edge overshoots past the whole
        // row — so it always appears to "snap left" regardless of where it was
        // actually dropped. Using the full chain's bounds instead makes the
        // corner-align reference match the whole visual row.
        var chainRowTiles = targets
            .map(function (t) { return t.data; })
            .filter(function (d) { return Math.abs(d.center.y - closestTileData.center.y) < ROW_Y_TOL; })
            .sort(function (a, b) { return a.center.x - b.center.x; });
        var closestChain = [closestTileData];
        var currentChain = [];
        for (var _h = 0, chainRowTiles_1 = chainRowTiles; _h < chainRowTiles_1.length; _h++) {
            var tile = chainRowTiles_1[_h];
            if (currentChain.length === 0) {
                currentChain = [tile];
            }
            else {
                var prevRightEdge = currentChain[currentChain.length - 1].center.x +
                    currentChain[currentChain.length - 1].dimensions.width;
                if (Math.abs(prevRightEdge - tile.center.x) < CHAIN_TOL) {
                    currentChain.push(tile);
                }
                else {
                    if (currentChain.some(function (t) { return t.id === closestTileData.id; }))
                        closestChain = currentChain;
                    currentChain = [tile];
                }
            }
        }
        if (currentChain.some(function (t) { return t.id === closestTileData.id; }))
            closestChain = currentChain;
        var refLeft = closestChain[0].center.x;
        var refRight = closestChain[closestChain.length - 1].center.x +
            closestChain[closestChain.length - 1].dimensions.width;
        var withinBounds = checkRight >= refLeft - SNAP_THRESHOLD && checkLeft <= refRight + SNAP_THRESHOLD;
        if (!withinBounds)
            return null;
        // Candidates: align the dragged tile's left edge OR right edge with
        // EVERY individual tile's left/right edge in the chain — not just the
        // chain's two outer corners. This lets a tile wider than a single row
        // tile (but not necessarily as wide as the whole row) "hang" flush
        // against any block boundary, e.g. a "1" tile dropped near the 3rd block
        // of a "1/12" row can align its left edge there and overhang the row's
        // right side, rather than only ever landing at the row's far corners.
        var candidates = [];
        for (var _j = 0, closestChain_1 = closestChain; _j < closestChain_1.length; _j++) {
            var tile = closestChain_1[_j];
            candidates.push(tile.center.x - groupLeftOffset);
            candidates.push(tile.center.x + tile.dimensions.width - virtualItem.dimensions.width - groupLeftOffset);
        }
        var leaderSnappedX = candidates[0];
        var minCandidateDist = Math.abs(pos.x - candidates[0]);
        for (var _k = 0, candidates_1 = candidates; _k < candidates_1.length; _k++) {
            var candidate = candidates_1[_k];
            var dist = Math.abs(pos.x - candidate);
            if (dist < minCandidateDist) {
                minCandidateDist = dist;
                leaderSnappedX = candidate;
            }
        }
        return {
            x: leaderSnappedX,
            y: targetY,
            isSnappedX: true,
            isSnappedY: true,
            rotation: undefined,
            modifications: [],
        };
    }
};
/**
 * Checks if two angular intervals overlap.
 * Angles in degrees [0, 360).
 */
var checkCircularOverlap = function (s1, sw1, s2, sw2) {
    // Normalize to [0, 360)
    var norm = function (a) { return ((a % 360) + 360) % 360; };
    // Relative start of 1 from 2
    var r1 = norm(s1 - s2);
    // If start of 1 is strictly inside 2
    if (r1 > EPSILON && r1 < sw2 - EPSILON)
        return true;
    // Relative end of 1 from 2
    var e1 = norm(s1 + sw1 - s2);
    if (e1 > EPSILON && e1 < sw2 - EPSILON)
        return true;
    // Check if 2 is inside 1 (case where 1 engulfs 2 completely)
    var r2 = norm(s2 - s1);
    if (r2 > EPSILON && r2 < sw1 - EPSILON)
        return true;
    return false;
};
/**
 * Snap a position to the background square grid.
 * Takes any x/y and returns the nearest grid-aligned x/y.
 */
export var snapToBackgroundGrid = function (x, y, BG_GRID_SIZE) {
    if (BG_GRID_SIZE === void 0) { BG_GRID_SIZE = GRID_SIZE; }
    return {
        x: Math.round(x / BG_GRID_SIZE) * BG_GRID_SIZE,
        y: Math.round(y / BG_GRID_SIZE) * BG_GRID_SIZE,
    };
};
/**
 * Snap a position to the nearest vertex of the equilateral triangle grid.
 * Vertices alternate between even rows (x = col*S) and odd rows (x = col*S + S/2).
 */
export var snapToTriangleGrid = function (x, y) {
    var S = GRID_SIZE;
    var H = (S * Math.sqrt(3)) / 2;
    var rowBelow = Math.floor(y / H);
    var bestDist = Infinity;
    var best = { x: x, y: y };
    for (var _i = 0, _a = [rowBelow, rowBelow + 1]; _i < _a.length; _i++) {
        var row = _a[_i];
        var vy = row * H;
        var xOffset = row % 2 === 0 ? 0 : S / 2;
        var col = Math.round((x - xOffset) / S);
        var vx = col * S + xOffset;
        var dist = Math.hypot(x - vx, y - vy);
        if (dist < bestDist) {
            bestDist = dist;
            best = { x: vx, y: vy };
        }
    }
    return best;
};
